// RadarChart.js
import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Radar } from 'react-chartjs-2';
import {
  Chart,
  PointElement,
  LineElement,
  Filler,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Box, Tooltip as MuiTooltip, useTheme, useMediaQuery } from '@mui/material';
import CustomRadialScale from './customRadialScale';

Chart.register(PointElement, LineElement, Filler, ChartTooltip, Legend, CustomRadialScale);

// Helper: hex -> rgba
const hexToRgba = (hex, alpha) => {
  const hexValue = hex.replace('#', '');
  const r = parseInt(hexValue.substring(0, 2), 16);
  const g = parseInt(hexValue.substring(2, 4), 16);
  const b = parseInt(hexValue.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Descripciones
const LABEL_DESCRIPTIONS = {
  Size: 'Number and distribution of professionals within the firm, reflecting its hierarchical structure, operational capacity, and institutional growth.',
  Visibility: "The ability of law firms to inspire trust, stand out in the legal market, and build client loyalty through its reputation, expertise, and values.",
  Trajectory: "Recognizes the time that firm’s key professionals have been operating in the market.",
  Arena: 'Measures the market conditions in which the firm operates.',
  'Hands-on Work': "Refers to practical, active involvement in tasks or projects to gain direct experience.",
  Reputation: "Firm's performance in major industry rankings, such as Chambers & Partners, The Legal 500, Who is Who Legal, among others.",
  Clients: 'Highlights the relevant players that trust in the firm.',
  Total: 'Overall performance and balance across all evaluated dimensions within the legal ecosystem.',
};

const RadarChart = ({ firm }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const chartRef = useRef(null);
  const [hitZones, setHitZones] = useState([]); // [{x,y,width,height,label,desc}]

  const primaryColor = '#F67147';

  const labels = useMemo(
    () => [
      'Size',
      'Visibility',
      'Trajectory',
      'Arena',
      'Hands-on Work',
      'Reputation',
      'Clients',
      'Total',
    ],
    []
  );

  const dataValues = useMemo(
    () => [
      firm?.size ?? 0,
      firm?.brandPower ?? 0,
      firm?.trajectory ?? 0,
      firm?.arena ?? 0,
      firm?.handsOnWork ?? 0,
      firm?.reputation ?? 0,
      firm?.clients ?? 0,
      firm?.totalScore ?? 0,
    ],
    [firm]
  );

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'score-shape',
          data: dataValues,
          fill: false,
          borderColor: primaryColor,
          borderWidth: 1,
          pointRadius: 0,
          pointHoverRadius: 0,
        },
      ],
    }),
    [labels, dataValues]
  );

  const options = useMemo(
    () => ({
      maintainAspectRatio: false,
      scales: {
        r: {
          type: 'customRadial',
          min: 0,
          max: 100,
          ticks: {
            beginAtZero: true,
            stepSize: 20,
            showLabelBackdrop: false,
            color: theme.palette.gray4,
            padding: 10,
            labelOffset: 5,
            font: {
              size: 0, // como tu original
              family: theme.typography.fontFamily,
              lineHeight: 1.5,
            },
          },
          angleLines: {
            display: true,
            color: theme.palette.gray4,
          },
          grid: {
            color: theme.palette.gray3,
          },
          pointLabels: {
            color: 'white',
            font: {
              size: isMobile ? 9 : 13,
              family: theme.typography.fontFamily,
            },
            callback: (label) => {
              if (label === 'Individual Performance') return ['Individual', 'Performance'];
              return label;
            },
          },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true, // (para la figura si quieres)
          backgroundColor: theme.palette.common.white,
          titleColor: theme.palette.gray1,
          bodyColor: theme.palette.gray2,
          borderWidth: 1,
          borderColor: theme.palette.gray4,
          titleFont: { size: 12, family: theme.typography.fontFamily },
          bodyFont: { size: 12, family: theme.typography.fontFamily },
        },
      },
    }),
    [theme, isMobile]
  );

  // Plugin de relleno con degradado (igual al tuyo)
  const gradientFillPlugin = useMemo(
    () => ({
      id: 'gradientFillPlugin',
      beforeDatasetsDraw: (chart) => {
        const { ctx, chartArea } = chart;
        if (!chartArea) return;
        const meta = chart.getDatasetMeta(0);
        const points = meta?.data;
        if (!points?.length) return;

        ctx.save();
        const gradient = ctx.createRadialGradient(
          chartArea.width / 2,
          chartArea.height / 2,
          0,
          chartArea.width / 2,
          chartArea.height / 2,
          chartArea.width / 2
        );
        gradient.addColorStop(0, hexToRgba(primaryColor, 0.2));
        gradient.addColorStop(0.5, hexToRgba(primaryColor, 0.7));
        gradient.addColorStop(1, hexToRgba(primaryColor, 0.9));

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      },
    }),
    []
  );

  // --- Cálculo de zonas de hover (invisibles) justo sobre cada label nativo ---
  const recomputeHitZones = () => {
    const chart = chartRef.current;
    if (!chart) return;
    const scale = chart.scales?.r;
    if (!scale) return;

    const zones = labels.map((label, i) => {
      // Posición en el borde (r.max)
      const base = scale.getPointPositionForValue(i, scale.max);
      // Empujar un poco más hacia afuera para coincidir con el texto (padding + labelOffset + extra)
      const pad = (scale.options?.pointLabels?.padding ?? 4) + (scale.options?.ticks?.labelOffset ?? 0) + 8;
      const cx = scale.xCenter;
      const cy = scale.yCenter;
      const vx = base.x - cx;
      const vy = base.y - cy;
      const mag = Math.hypot(vx, vy) || 1;
      const x = base.x + (vx / mag) * pad;
      const y = base.y + (vy / mag) * pad;

      // Tamaño del "hit" (invisible). Puedes ajustar según tu tipografía.
      const w = isMobile ? 52 : 66;
      const h = isMobile ? 16 : 20;

      return {
        x,
        y,
        width: w,
        height: h,
        label,
        desc: LABEL_DESCRIPTIONS[label] ?? label,
        value: Number(dataValues[i]).toFixed(1),
      };
    });

    setHitZones(zones);
  };

  // Recalcular cuando cambie el tamaño del canvas o datos
  useEffect(() => {
    recomputeHitZones();
    // Observa resize del canvas
    const chart = chartRef.current;
    const handleResize = () => recomputeHitZones();
    if (chart) {
      // react-chartjs-2 expone un listener onResize; por si acaso nos apoyamos también en ResizeObserver
      const ro = new ResizeObserver(handleResize);
      ro.observe(chart.canvas);
      return () => ro.disconnect();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataValues, isMobile]);

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* El Radar mantiene los labels nativos (estética original) */}
      <Radar ref={chartRef} data={data} options={options} plugins={[gradientFillPlugin]} />

      {/* Zonas invisibles: activan MUI Tooltip sobre cada label nativo */}
      {hitZones.map((z, idx) => (
        <MuiTooltip
          key={`${z.label}-${idx}`}
          title={
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <strong>{z.label}</strong>
              <span>Score: {z.value}</span>
              <span>{z.desc}</span>
            </Box>
          }
          arrow
          placement="top"
        >
          <Box
            sx={{
              position: 'absolute',
              left: z.x,
              top: z.y,
              width: z.width,
              height: z.height,
              transform: 'translate(-50%, -50%)',
              // invisibles:
              backgroundColor: 'transparent',
              pointerEvents: 'auto',
              // Por si hay overlays: que tenga prioridad de hover pero sin alterar layout
              zIndex: 2,
            }}
          />
        </MuiTooltip>
      ))}
    </Box>
  );
};

export default RadarChart;
